import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from '@techstyle/react-intl';
import { useProductFilters } from '../ProductFilterContext';
import { useProductFilters as useProductFiltersCustomFilters } from '../ProductFilterContextCustomFilters';
import ProductListingFilter, { ProductListingFilterStyle } from '../ProductListingFilter';
import { parseBraSize, parseBraSizes } from '../utils/sizes';
var BraSizeLabel = styled.span.withConfig({
  displayName: "ProductSizeFilter__BraSizeLabel",
  componentId: "sc-10h2dpr-0"
})(["\n  display: flex;\n  flex-direction: column;\n"]);
var BandSize = styled.span.withConfig({
  displayName: "ProductSizeFilter__BandSize",
  componentId: "sc-10h2dpr-1"
})(["\n  font-size: 10px;\n  opacity: 0.75;\n  margin: -6px 0 2px 0;\n"]);
/**
 * A special version of `ProductListingFilter` with custom logic for separating
 * the `size_bra` aggregation filter into Band Size and Cup Size selections.
 */

var ProductSizeFilter = _ref => {
  var _selectedFilters$filt;

  var _ref$as = _ref.as,
      ElementType = _ref$as === void 0 ? ProductListingFilter : _ref$as,
      inputFilterField = _ref.filterField,
      inputSelectedFilters = _ref.selectedFilters,
      bandSizeCheckboxProps = _ref.bandSizeCheckboxProps,
      cupSizeCheckboxProps = _ref.cupSizeCheckboxProps,
      _ref$withNestedBraFil = _ref.withNestedBraFilter,
      withNestedBraFilter = _ref$withNestedBraFil === void 0 ? true : _ref$withNestedBraFil,
      onChange = _ref.onChange,
      onFilterSizeClicked = _ref.onFilterSizeClicked,
      rest = _objectWithoutProperties(_ref, ["as", "filterField", "selectedFilters", "bandSizeCheckboxProps", "cupSizeCheckboxProps", "withNestedBraFilter", "onChange", "onFilterSizeClicked"]);

  // These two contexts are identical for the purposes of what this
  // component does with them, but we need to check both to maintain
  // backwards compatibility.
  var filterContext = useProductFilters();
  var customFilterContext = useProductFiltersCustomFilters();
  filterContext = filterContext || customFilterContext;

  if (typeof inputSelectedFilters === 'function') {
    if (filterContext) {
      inputSelectedFilters = inputSelectedFilters(filterContext.selectedFilters);
    } else {
      inputSelectedFilters = inputSelectedFilters();
    }
  } else if (!inputSelectedFilters) {
    inputSelectedFilters = filterContext.selectedFilters;
  }

  var inputBraFilterField = useMemo(() => {
    if (inputFilterField.field === 'size_bra') {
      return inputFilterField;
    } else if (inputFilterField.nestedFilters) {
      return inputFilterField.nestedFilters.find(subFilter => subFilter.field === 'size_bra');
    }
  }, [inputFilterField]);
  var selectedCupSizesByBand = useMemo(() => inputBraFilterField ? parseBraSizes(inputSelectedFilters[inputBraFilterField.field]) : {}, [inputBraFilterField, inputSelectedFilters]);
  var cupSizesByBand = useMemo(() => inputBraFilterField ? parseBraSizes(inputBraFilterField.items.map(item => item.value)) : {}, [inputBraFilterField]);
  var selectableBandSizes = useMemo(() => Object.keys(cupSizesByBand).map(bandSize => ({
    value: bandSize
  })), [cupSizesByBand]);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      lastSelectedBand = _useState2[0],
      setSelectedBand = _useState2[1];

  var activeBand = useMemo(() => {
    if ( // If no explicit selection, stay in sync with selected and selectable
    // sizes.
    lastSelectedBand == null || // If the last selected option is no longer selectable, prevent from
    // using stale value.
    !selectableBandSizes.some(bandSize => bandSize.value === lastSelectedBand)) {
      var firstBand = Object.keys(selectedCupSizesByBand)[0];

      if (!firstBand && selectableBandSizes.length) {
        firstBand = selectableBandSizes[0].value;
      }

      return firstBand || null;
    }

    return lastSelectedBand;
  }, [lastSelectedBand, selectableBandSizes, selectedCupSizesByBand]);
  var selectableCupSizes = useMemo(() => activeBand && cupSizesByBand[activeBand] ? cupSizesByBand[activeBand].map(cupSize => ({
    label: /*#__PURE__*/React.createElement(BraSizeLabel, null, /*#__PURE__*/React.createElement(BandSize, null, activeBand), /*#__PURE__*/React.createElement("span", null, cupSize)),
    value: cupSize
  })) : [], [activeBand, cupSizesByBand]);
  var filterField = useMemo(() => {
    if (inputBraFilterField && withNestedBraFilter) {
      var isTopLevel = inputFilterField === inputBraFilterField;
      var emptyMessage = inputBraFilterField.totalAggregationCount !== 0 ? /*#__PURE__*/React.createElement(FormattedMessage, {
        id: "site_product_filter.select_band_size",
        defaultMessage: "Select a band size."
      }) : null;
      var braFields = [{
        label: isTopLevel ? 'Band Size' : 'Bra Band Size',
        field: 'band_size',
        filterCount: 0,
        filterStyle: ProductListingFilterStyle.GRID,
        items: selectableBandSizes,
        totalAggregationCount: inputBraFilterField.totalAggregationCount,
        bandSizeCheckboxProps
      }, {
        label: isTopLevel ? 'Cup Size' : 'Bra Cup Size',
        field: 'cup_size',
        filterCount: inputSelectedFilters[inputBraFilterField.field].length,
        filterStyle: ProductListingFilterStyle.GRID,
        items: selectableCupSizes,
        totalAggregationCount: inputBraFilterField.totalAggregationCount,
        emptyMessage: emptyMessage,
        cupSizeCheckboxProps
      }];

      if (isTopLevel) {
        return _objectSpread(_objectSpread({}, inputFilterField), {}, {
          nestedFilters: braFields,
          items: null
        });
      } else {
        var nestedFilters = inputFilterField.nestedFilters.slice(); // Replace `size_bra` with these filters.

        var braIndex = nestedFilters.indexOf(inputBraFilterField);
        nestedFilters.splice(braIndex, 1, ...braFields);
        return _objectSpread(_objectSpread({}, inputFilterField), {}, {
          nestedFilters
        });
      }
    } else {
      return inputFilterField;
    }
  }, [bandSizeCheckboxProps, cupSizeCheckboxProps, inputBraFilterField, inputFilterField, inputSelectedFilters, selectableBandSizes, selectableCupSizes, withNestedBraFilter]);
  var selectedFilters = useMemo(() => inputBraFilterField ? _objectSpread(_objectSpread({}, inputSelectedFilters), {}, {
    band_size: activeBand,
    cup_size: activeBand ? selectedCupSizesByBand[activeBand] || [] : []
  }) : inputSelectedFilters, [activeBand, inputBraFilterField, inputSelectedFilters, selectedCupSizesByBand]);
  var selectedFieldFilters = (_selectedFilters$filt = selectedFilters[filterField.field]) !== null && _selectedFilters$filt !== void 0 ? _selectedFilters$filt : [];

  var _selectedFieldFilters = selectedFieldFilters.slice(-1),
      _selectedFieldFilters2 = _slicedToArray(_selectedFieldFilters, 1),
      lastSelectedItem = _selectedFieldFilters2[0];

  var prevLastSelectedItem = useRef(lastSelectedItem);
  var isLastSelectedItemChanging = lastSelectedItem !== prevLastSelectedItem.current;
  useEffect(() => {
    if (isLastSelectedItemChanging) {
      if (inputFilterField.field === 'size_bra') {
        if (lastSelectedItem) {
          var _parseBraSize = parseBraSize(lastSelectedItem),
              _parseBraSize2 = _slicedToArray(_parseBraSize, 1),
              bandSize = _parseBraSize2[0];

          if (bandSize !== lastSelectedBand) {
            setSelectedBand(bandSize);
          }
        } else {
          setSelectedBand(null);
        }
      }

      prevLastSelectedItem.current = lastSelectedItem;
    }
  }, [isLastSelectedItemChanging, inputFilterField.field, lastSelectedBand, lastSelectedItem]);
  var handleFilterChange = useCallback(_ref2 => {
    var filterField = _ref2.filterField,
        newSelectedFilters = _ref2.newSelectedFilters,
        newSelectedFilterValue = _ref2.newSelectedFilterValue,
        newAllSelectedFilters = _ref2.newAllSelectedFilters,
        event = _ref2.event,
        rest = _objectWithoutProperties(_ref2, ["filterField", "newSelectedFilters", "newSelectedFilterValue", "newAllSelectedFilters", "event"]);

    switch (filterField.field) {
      case 'band_size':
        setSelectedBand(newSelectedFilters);
        break;

      case 'cup_size':
        {
          var newBraSizes = _objectSpread(_objectSpread({}, selectedCupSizesByBand), {}, {
            [activeBand]: newSelectedFilters
          }); // Convert back into a flat size list like the parent expects.


          var newSizeArray = [];

          for (var bandSize in newBraSizes) {
            for (var cupSize of newBraSizes[bandSize]) {
              newSizeArray.push("".concat(bandSize).concat(cupSize));
            }
          }

          var updatedNewSizesFilters = _objectSpread(_objectSpread({}, newAllSelectedFilters), {}, {
            size_bra: event.target.type === 'radio' ? "".concat(activeBand).concat(newSelectedFilterValue) : newSizeArray
          });

          onFilterSizeClicked && onFilterSizeClicked({
            newSelectedFilterValue,
            newAllSelectedFilters: updatedNewSizesFilters
          });
          onChange(_objectSpread({
            filterField: inputBraFilterField,
            newSelectedFilters: newSizeArray
          }, rest));
          break;
        }

      default:
        onFilterSizeClicked && onFilterSizeClicked({
          newSelectedFilterValue,
          newAllSelectedFilters
        });
        onChange(_objectSpread({
          filterField,
          newSelectedFilters: event.target.type === 'radio' ? [newSelectedFilterValue] : newSelectedFilters,
          newSelectedFilterValue
        }, rest));
    }
  }, [activeBand, inputBraFilterField, onChange, onFilterSizeClicked, selectedCupSizesByBand]);
  return /*#__PURE__*/React.createElement(ElementType, _extends({
    filterField: filterField,
    selectedFilters: selectedFilters,
    onChange: handleFilterChange
  }, rest));
};

ProductSizeFilter.propTypes = {
  as: PropTypes.elementType,
  bandSizeCheckboxProps: PropTypes.object,
  cupSizeCheckboxProps: PropTypes.object,
  filterField: PropTypes.object,
  onChange: PropTypes.func,
  onFilterSizeClicked: PropTypes.func,
  selectedFilters: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  withNestedBraFilter: PropTypes.bool
};
ProductSizeFilter.BandSize = BandSize;
export default ProductSizeFilter;